import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Button, TextField, Box, Typography, Grid, Paper, Dialog, DialogActions, DialogTitle, DialogContent } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useSiteSettings } from '../context/SiteSettingsContext';




type BooleanKeys =
  | "active"
  | "for_intake"
  | "for_progress"
  | "for_biography"
  | "for_individual"
  | "for_couple"
  | "for_group"

  | "for_report"
  | "for_dialogue"
  | "for_vt"
  | "for_st"
  | "for_tp";

export default function TemplatesPage() {
  // TemplateCard-Komponente (memoized)
  const TemplateCard = React.memo(({ template, selectedTemplate, onSelect, onDelete, onDuplicate }: any) => (
    <Paper
      sx={{
        width: "100%",
        padding: "8px 10px",
        cursor: "pointer",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: selectedTemplate?.id === template.id ? "#f0f0f0" : "#ffffff",
        border: "1px solid #ddd",
        borderRadius: "6px",
        "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.15)" },
      }}
      onClick={() => onSelect(template)}
    >
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, overflow: "hidden" }}>
        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
          {template.name}
        </Typography>
        <Typography variant="caption" sx={{ color: "gray", fontSize: "0.7rem" }}>
          {[
            template.for_individual && "Einzel",
            template.for_couple && "Paar",
            template.for_group && "Gruppe",
          ].filter(Boolean).join(" & ") || "—"}{" | "}
          {[
            template.for_dialogue && "Dialog",
            template.for_report && "Report",
          ].filter(Boolean).join(" & ") || "—"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: template.active ? "green" : "red",
          }}
        />
        <ContentCopyIcon
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(template);
          }}
          sx={{ fontSize: 16, color: "blue", cursor: "pointer" }}
        />
        <DeleteIcon
          onClick={(e) => {
            e.stopPropagation();
            onDelete(template.id);
          }}
          sx={{ fontSize: 16, color: "red", cursor: "pointer" }}
        />
      </Box>
    </Paper>
  ));

  // Hilfsfunktion zum Rendern einer TemplateCard
  const renderTemplateCard = (template: any) => (
    <Grid item key={template.id} xs={12}>
      <TemplateCard
        template={template}
        selectedTemplate={selectedTemplate}
        onSelect={handleSelectTemplate}
        onDelete={handleOpenDeleteDialog}
        onDuplicate={handleDuplicateTemplate}
      />
    </Grid>
  );
  const handleDuplicateTemplate = async (template: any) => {
    try {
      const newTemplate = {
        ...template,
        id: undefined,
        name: `${template.name} (Kopie)`,
        internal_name: `${template.internal_name || template.name}_copy`,
      };
      await axios.post("/api/apps/add-template/", newTemplate);
      const updatedTemplates = await axios.get("/api/apps/fetch-templates");
      setTemplates(updatedTemplates.data);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1)
        console.error("Fehler beim Duplizieren:", error);
    }
  };
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    internal_name: "",
    shared_prompt_text: "",
    role_prompt: "",
    template_text: "",
    template_text_part_2: "",
    template_revision_text: "",
    template_format_text: "",
    json_structure: "",
    active: false,
    for_intake: false,
    for_progress: false,
    for_biography: false,
    for_individual: false,
    for_couple: false,
    for_group: false,
    for_report: false,
    for_dialogue: false,
    for_vt: false,
    for_st: false,
    for_tp: false
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<number | null>(null);
  // Fetch Templates API Call
  const fetchTemplates = async () => {
    try {
      const response = await axios.get("/api/apps/fetch-templates");
      setTemplates(response.data);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Abrufen der Templates:", error);
    }
  };

  // Nur initial laden, kein Intervall mehr
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line
  }, []);

  // Auswahl eines Templates
  const handleSelectTemplate = (template: any) => {
    setSelectedTemplate(template);
    // Ensure role_prompt is present (default to empty string if missing)
    setFormData({
      ...template,
      role_prompt: template.role_prompt ?? "",
    });
  };

  // Änderungen in der Form erfassen
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };



  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  // Speichern der Änderungen (Update)
  const handleSave = async () => {
    if (!formData.id) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Kein Template ausgewählt.");
      return;
    }

    try {
      const response = await axios.put("/api/apps/update-template/", {
        id: formData.id,
        name: formData.name,
        internal_name: formData.internal_name,
        shared_prompt_text: formData.shared_prompt_text,
        role_prompt: formData.role_prompt,
        template_text: formData.template_text,
        template_text_part_2: formData.template_text_part_2,
        template_revision_text: formData.template_revision_text,
        template_format_text: formData.template_format_text,
        json_structure: formData.json_structure,
        active: formData.active,
        for_intake: formData.for_intake,
        for_progress: formData.for_progress,
        for_biography: formData.for_biography,
        for_individual: formData.for_individual,
        for_couple: formData.for_couple,
        for_group: formData.for_group,
        for_report: formData.for_report,
        for_dialogue: formData.for_dialogue,
        for_vt: formData.for_vt,
        for_st: formData.for_st,
        for_tp: formData.for_tp
      });

      if (!isSiteSettingsLoading && logLevel >= 3) console.log("Template aktualisiert:", response.data);

      // Nach dem Update die Templates erneut abrufen
      const updatedTemplates = await axios.get("/api/apps/fetch-templates");
      setTemplates(updatedTemplates.data);

    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Speichern des Templates:", error);
    }
  };

  // Neues Template erstellen
  const handleCreateNew = async () => {
    try {
      const response = await axios.post("/api/apps/add-template/", {
        name: formData.name,
        shared_prompt_text: formData.shared_prompt_text,
        role_prompt: formData.role_prompt,
        template_text: formData.template_text,
        template_text_part_2: formData.template_text_part_2,
        template_revision_text: formData.template_revision_text,
        template_format_text: formData.template_format_text,
        json_structure: formData.json_structure,
        active: formData.active,
        for_intake: formData.for_intake,
        for_progress: formData.for_progress,
        for_biography: formData.for_biography,
        for_individual: formData.for_individual,
        for_couple: formData.for_couple,
        for_group: formData.for_group,
        for_report: formData.for_report,
        for_dialogue: formData.for_dialogue,
        for_vt: formData.for_vt,
        for_st: formData.for_st,
        for_tp: formData.for_tp
      });

      // Daten erneut abrufen
      const updatedTemplates = await axios.get("/api/apps/fetch-templates");
      setTemplates(updatedTemplates.data);

      // Formular leeren
      setFormData({
        id: "",
        name: "",
        internal_name: "",
        shared_prompt_text: "",
        role_prompt: "",
        template_text: "",
        template_text_part_2: "",
        template_revision_text: "",
        template_format_text: "",
        json_structure: "",
        active: false,
        for_intake: false,
        for_progress: false,
        for_biography: false,
        for_individual: false,
        for_couple: false,
        for_group: false,
        for_report: false,
        for_dialogue: false,
        for_vt: false,
        for_st: false,
        for_tp: false
      });

    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Erstellen:", error);
    }
  };
  const handleOpenDeleteDialog = (templateId: number) => {
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteTemplate = async (templateId: number) => {
    try {
      await axios.delete(`/api/apps/delete-template/${templateId}/`);
      // Templates erneut abrufen
      const updatedTemplates = await axios.get("/api/apps/fetch-templates");
      setTemplates(updatedTemplates.data);
      handleClearForm();
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Löschen:", error);
    }
  };

  const handleClearForm = () => {
    setSelectedTemplate(null); // Setzt die Template-Auswahl zurück
    setFormData({
      id: "",
      name: "",
      internal_name: "",
      shared_prompt_text: "",
      role_prompt: "",
      template_text: "",
      template_text_part_2: "",
      template_revision_text: "",
      template_format_text: "",
      json_structure: "",
      active: false,
      for_intake: false,
      for_progress: false,
      for_biography: false,
      for_individual: false,
      for_couple: false,
      for_group: false,
      for_report: false,
      for_dialogue: false,
      for_vt: false,
      for_st: false,
      for_tp: false
    }); // Setzt die Formularfelder auf leer
  };

  // Helper function to generate readable labels for boolean flags
  const getLabelForFlag = (flag: string) => {
    return flag
      .replace("for_", "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Define arrays for grouzping keys
  const noteTypes = [
    { key: "for_intake", label: "Intake" },
    { key: "for_progress", label: "Progress" },
    { key: "for_biography", label: "Biography" },
  ];

  const therapyForms = [
    { key: "for_vt", label: "Verhaltenstherapie" },
    { key: "for_st", label: "Systemisch" },
    { key: "for_tp", label: "Tiefenpsychologisch" },
  ];

  const clientTypesAndFormats = [
    "for_individual",
    "for_group",
    "for_couple",
    "for_report",
    "for_dialogue",
  ];

  // Gruppierung der Templates nach Therapieform, dann Dokuart
  const groupedTemplates = useMemo(() => {
    const result: any = {
      vt: { intake: [], progress: [], biography: [], unassigned: [] },
      st: { intake: [], progress: [], biography: [], unassigned: [] },
      tp: { intake: [], progress: [], biography: [], unassigned: [] },
      unassigned: { intake: [], progress: [], biography: [], unassigned: [] },
    };

    templates.forEach((t) => {
      let therapyAssigned = false;
      therapyForms.forEach((therapy) => {
        if (t[therapy.key]) {
          const formShort = therapy.key.replace("for_", "");
          let noteAssigned = false;
          noteTypes.forEach((note) => {
            if (t[note.key]) {
              const noteShort = note.key.replace("for_", "");
              result[formShort][noteShort].push(t);
              noteAssigned = true;
            }
          });
          if (!noteAssigned) {
            result[formShort].unassigned.push(t);
          }
          therapyAssigned = true;
        }
      });

      if (!therapyAssigned) {
        let noteAssigned = false;
        noteTypes.forEach((note) => {
          if (t[note.key]) {
            const noteShort = note.key.replace("for_", "");
            result.unassigned[noteShort].push(t);
            noteAssigned = true;
          }
        });
        if (!noteAssigned) {
          result.unassigned.unassigned.push(t);
        }
      }
    });

    return result;
  }, [templates]);

  return (
    <Box
      sx={{
        padding: "20px",
        height: "100vh",          // Volle Höhe des Viewports
        overflow: "auto",         // Scrollbar bei Bedarf
        boxSizing: "border-box"
      }}  >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ flexGrow: 1 }}>Templates</Typography>
        <Button variant="outlined" onClick={fetchTemplates} title="Neu laden">
          🔄 Neu laden
        </Button>
      </Box>

      {/* Template Liste */}
      <Box>
        <Grid container spacing={4}>
          {therapyForms.map((therapyForm) => {
            const formShort = therapyForm.key.replace("for_", "");
            const group = groupedTemplates[formShort];
            return (
              <Grid item xs={12} md={4} key={therapyForm.key}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    {therapyForm.label}
                  </Typography>

                  {noteTypes.map((noteType) => {
                    const noteShort = noteType.key.replace("for_", "");
                    const templatesForNoteType = group[noteShort];
                    if (!templatesForNoteType || templatesForNoteType.length === 0) return null;
                    return (
                      <Box key={noteType.key} sx={{ mb: 3, pl: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          {noteType.label}
                        </Typography>
                        <Grid container spacing={2}>
                          {templatesForNoteType.map((template: any) => renderTemplateCard(template))}
                        </Grid>
                      </Box>
                    );
                  })}

                  {/* Templates mit Therapieform aber ohne NoteType */}
                  <Box sx={{ mt: 2, pl: 2 }}>
                    {group.unassigned.length > 0 && (
                      <>
                        <Typography variant="subtitle1" gutterBottom>Nicht zugeordnet</Typography>
                        <Grid container spacing={2}>
                          {group.unassigned.map((template: any) => renderTemplateCard(template))}
                        </Grid>
                      </>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Templates ohne Therapieform */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Keiner Therapieform zugeordnet</Typography>
          <Grid container spacing={2}>
            {[
              ...groupedTemplates.unassigned.intake,
              ...groupedTemplates.unassigned.progress,
              ...groupedTemplates.unassigned.biography,
              ...groupedTemplates.unassigned.unassigned,
            ].map((template: any) => (
              <Grid item key={template.id} xs={12} sm={6} md={4}>
                <TemplateCard
                  template={template}
                  selectedTemplate={selectedTemplate}
                  onSelect={handleSelectTemplate}
                  onDelete={handleOpenDeleteDialog}
                  onDuplicate={handleDuplicateTemplate}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Neues Template hinzufügen */}
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => handleClearForm()}>
            ➕ Neues Template
          </Button>
        </Box>
      </Box>

      {/* Eingabefelder für Templates (Immer sichtbar) */}
      <Box sx={{ marginTop: "20px" }}>
        <Typography variant="h5">{selectedTemplate ? "Template bearbeiten" : "Neues Template erstellen"}</Typography>

        <TextField
          fullWidth
          margin="normal"
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Interner Name oder Bescheibung"
          name="internal_name"
          value={formData.internal_name}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={80}
          margin="normal"
          label="Rollen-Prompt (z. B. Therapeut:innenrolle)"
          name="role_prompt"
          value={formData.role_prompt}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          multiline
          minRows={4} // Startet mit 4 Zeilen
          maxRows={80} // Maximal 50 Zeilen
          margin="normal"
          label="Gemeinsamer Prompt Text (zB Sprachstil, Ethik, etc.)"
          name="shared_prompt_text"
          value={formData.shared_prompt_text}
          onChange={handleChange}
        />
        
        <TextField
          fullWidth
          multiline
          minRows={4} // Startet mit 4 Zeilen
          maxRows={80} // Maximal 50 Zeilen
          margin="normal"
          label="Template Doku-Promt Teil 1"
          name="template_text"
          value={formData.template_text}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          multiline
          minRows={4} // Startet mit 4 Zeilen
          maxRows={80} // Maximal 50 Zeilen
          margin="normal"
          label="Template Doku-Promt Teil 2 (falls leer wird automatisch nur Teil 1 genutzt)"
          name="template_text_part_2"
          value={formData.template_text_part_2}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          multiline
          minRows={4} // Startet mit 4 Zeilen
          maxRows={80} // Maximal 50 Zeilen
          margin="normal"
          label="Template Revisions-Prompt (falls leer wird kein Revisionsschritt genutzt)"
          name="template_revision_text"
          value={formData.template_revision_text}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          multiline
          minRows={4} // Startet mit 4 Zeilen
          maxRows={80} // Maximal 50 Zeilen
          margin="normal"
          label="Template Format-Prompt (falst leer wird kein Formatierungsschritt genutzt)"
          name="template_format_text"
          value={formData.template_format_text}
          onChange={handleChange}
        />

        <TextField
          fullWidth
          multiline
          minRows={4} // Startet mit 4 Zeilen
          maxRows={50} // Maximal 50 Zeilen
          margin="normal"
          label="JSON Struktur"
          name="json_structure"
          value={formData.json_structure}
          onChange={handleChange}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>

          {/* Aktiv */}
          <Box>
            <Typography variant="subtitle1">Status</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  name="active"
                  checked={formData.active}
                  onChange={handleCheckboxChange}
                />
              }
              label="Aktiv"
            />
          </Box>

          {/* Intake, Progress, Biography */}
          <Box>
            <Typography variant="subtitle1">Dokumentationsart</Typography>
            {["for_intake", "for_progress", "for_biography"].map((key) => {
              const keyTyped = key as BooleanKeys;
              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      name={key}
                      checked={formData[keyTyped]}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={key.replace("for_", "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                />
              );
            })}
          </Box>

          {/* Individual, Couple, Group */}
          <Box>
            <Typography variant="subtitle1">Kliententyp</Typography>
            {["for_individual", "for_couple", "for_group"].map((key) => {
              const keyTyped = key as BooleanKeys;
              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      name={key}
                      checked={formData[keyTyped]}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={key.replace("for_", "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                />
              );
            })}
          </Box>

          {/* Report, Dialogue */}
          <Box>
            <Typography variant="subtitle1">Ausgabeformat</Typography>
            {["for_report", "for_dialogue"].map((key) => {
              const keyTyped = key as BooleanKeys;
              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      name={key}
                      checked={formData[keyTyped]}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={key.replace("for_", "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                />
              );
            })}
          </Box>

          {/* Therapieform */}
          <Box>
            <Typography variant="subtitle1">Therapieform</Typography>
            {["for_vt","for_st","for_tp"].map((key) => {
              const keyTyped = key as BooleanKeys;
              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      name={key}
                      checked={formData[keyTyped]}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={key.replace("for_", "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                />
              );
            })}
          </Box>

        </Box>

        {/* Buttons */}
        {selectedTemplate ? (
          <Button variant="contained" color="primary" onClick={handleSave} sx={{ marginTop: "10px" }}>
            Änderungen speichern
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={handleCreateNew} sx={{ marginTop: "10px" }}>
            ➕ Template hinzufügen
          </Button>
        )}
      </Box>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Template löschen</DialogTitle>
        <DialogContent>
          <Typography>Soll das Template wirklich gelöscht werden?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">Abbrechen</Button>
          <Button onClick={() => templateToDelete !== null && handleDeleteTemplate(templateToDelete)} color="error">
            Löschen
          </Button>        </DialogActions>
      </Dialog>
    </Box>
  );
}